import { useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useOutletContext } from 'react-router-dom';
import { useAuthStore } from '@/stores';
import { ROUTES } from '@/constants';
import { Spinner } from '@/components/ui';
import type { EffectiveRole } from '@/types/auth';
import styles from './RoleProtectedRoute.module.css';

interface RoleProtectedRouteProps {
  allowedRoles: EffectiveRole[];
  redirectTo?: string;
}

export function RoleProtectedRoute({ allowedRoles, redirectTo }: RoleProtectedRouteProps) {
  const { isLogged, isLoading, getUser, fetchUser, getEffectiveRole } = useAuthStore();
  const [isChecking, setIsChecking] = useState(false);
  const hasFetched = useRef(false);
  const user = getUser();
  const outletContext = useOutletContext();

  // Fetch user if not loaded yet
  useEffect(() => {
    if (isLogged && !user && !isLoading) {
      setIsChecking(true);
      fetchUser().finally(() => setIsChecking(false));
    }
  }, [isLogged, user, isLoading, fetchUser]);

  // Re-fetch on mount for unassigned users so role grants
  // take effect on page refresh without requiring logout
  useEffect(() => {
    if (isLogged && user && !hasFetched.current && getEffectiveRole() === 'unassigned') {
      hasFetched.current = true;
      setIsChecking(true);
      fetchUser().finally(() => setIsChecking(false));
    }
  }, [isLogged, user, fetchUser, getEffectiveRole]);

  // Show loading while checking authentication or fetching user
  if (isLoading || (isLogged && !user && isChecking)) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="lg" variant="primary" />
        <p>Loading...</p>
      </div>
    );
  }

  // Not logged in - redirect to login
  if (!isLogged) {
    return <Navigate to={redirectTo ?? ROUTES.login} replace />;
  }

  // Attempted fetch failed or user is really null
  if (!user) {
    return <Navigate to={redirectTo ?? ROUTES.login} replace />;
  }

  // Logged in but role not allowed - show access denied
  const role = getEffectiveRole();
  if (!allowedRoles.includes(role)) {
    return (
      <div className={styles.accessDenied}>
        <div className={styles.accessDeniedCard}>
          <h1>Access Denied</h1>
          <p>You do not have permission to access this page.</p>
          <button
            onClick={() => (window.location.href = ROUTES.home)}
            className={styles.backButton}
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return <Outlet context={outletContext} />;
}
